import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  IoIosWarning, IoMdClose, IoIosInformationCircle, IoMdAlert,
} from 'react-icons/io';
import { useAnalytics } from 'use-analytics';
import { setUserPref } from '../../store/slices/user';
import MarkdownToHtml from '../MarkdownToHtml';
import { placementOptions } from '../../constants';

const levels = {
  warning: {
    bg: 'bg-orange-200', border: 'border-orange-500', icon: IoIosWarning, iconColor: 'text-yellow-700',
  },
  alert: {
    bg: 'bg-red-200', border: 'border-red-500', icon: IoMdAlert, iconColor: 'text-red-700',
  },
  info: {
    bg: 'bg-blue-200', border: 'border-blue-500', icon: IoIosInformationCircle, iconColor: 'text-blue-700',
  },
};

const AgencyNotification = ({ notification, placement, className }) => {
  const { track } = useAnalytics();
  const dispatch = useDispatch();

  const [isExpanded, setIsExpanded] = useState(false);
  const { email, userPreferences } = useSelector((state) => state.user);

  const {
    id: notificationId, title, details, level,
  } = notification || {};

  if (!title) return <></>;

  const {
    bg, border, icon: IconComponent, iconColor,
  } = levels[level?.toLowerCase()];

  const handleDismiss = () => {
    const updatedDismissedNotifications = [
      ...(userPreferences?.dismissedNotifications || []),
      { id: notificationId, placement },
    ];
    dispatch(setUserPref({ dismissedNotifications: updatedDismissedNotifications }));
    track('agency_notification_dismissed', { user: email, notificationId, placement });
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      track('agency_notification_expanded', { user: email, notificationId, placement });
    }
  };

  return (
    <div className={`relative flex flex-col p-2 rounded my-4 border-l-8 ${bg} ${border} ${className} ${(placement === placementOptions.TOP) ? 'text-center' : ''}`}>
      <div className={`flex flex-shrink-0 mr-3  ${(placement === placementOptions.TOP) ? 'text-center justify-center' : ''}`}>
        <IconComponent className={`mr-2 w-6 h-6 mt-1 min-w-8 ${iconColor}`} />
        <MarkdownToHtml content={title} className="font-bold mt-0 max-w-none" />
      </div>
      <div className={`flex-grow ${placement === placementOptions.TOP && isExpanded ? 'text-left mt-3' : ''}`}>
        {details && (
        <div>
          {isExpanded ? (<MarkdownToHtml content={(details || '')} className="text-sm text-gray-800 ml-10" />) : <></>}
          {details.length > 100 && (
          <button type="button" onClick={handleToggleExpand} className="bg-transparent ml-10 text-blue-400 border-none cursor-pointer p-0 mt-1 text-sm focus:outline-none focus:ring-0">
            {isExpanded ? 'Show Less' : 'Show More'}
          </button>
          )}
        </div>
        )}
      </div>
      <button type="button" onClick={handleDismiss} className="absolute top-0 mt-1 right-0 mr-1 p-1 bg-transparent text-gray-800 border-none hover:text-black hover:font-bold focus:outline-none focus:ring-0">
        <IoMdClose className="w-5 h-5" />
      </button>
    </div>
  );
};

AgencyNotification.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    details: PropTypes.string,
  }).isRequired,
  placement: PropTypes.oneOf(Object.values(placementOptions)).isRequired,
  className: PropTypes.string,
};

AgencyNotification.defaultProps = {
  className: '',
};

export default AgencyNotification;
